import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Handle navigation based on auth state changes
        if (event === 'SIGNED_IN' && session) {
          // Check for pending team invitation token - redirect to client dashboard
          const pendingTeamInvite = localStorage.getItem('pending_team_invitation_token');
          const pendingClientInvite = localStorage.getItem('pending_client_portal_invite');
          const pendingClientSession = localStorage.getItem('pending_client_portal_session');
          
          if (pendingTeamInvite || pendingClientInvite || pendingClientSession) {
            // Redirect to client dashboard to handle pending invitation
            setTimeout(() => {
              navigate('/client-dashboard');
            }, 100);
            return;
          }
          
          // Check for redirect/next parameter in URL or pending redirect from localStorage
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = urlParams.get('redirect') || urlParams.get('next');
          const pendingRedirect = localStorage.getItem('pendingAuthRedirect');

          const isSafe = (p: string | null) => !!p && p.startsWith('/') && !p.startsWith('//');
          const target = isSafe(redirectUrl) ? redirectUrl : (isSafe(pendingRedirect) ? pendingRedirect : null);

          if (target) {
            localStorage.removeItem('pendingAuthRedirect');
            navigate(target);
          } else if (location.pathname === '/auth' || location.pathname === '/') {
            navigate('/dashboard');
          }
        } else if (event === 'SIGNED_OUT') {
          // Redirect to index page on sign out
          navigate('/');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // If user is already logged in and on index/auth page, redirect to dashboard
      if (session?.user && (location.pathname === '/' || location.pathname === '/auth')) {
        navigate('/dashboard');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName
        }
      }
    });

    // Add user to Resend audience for product updates
    if (!error) {
      try {
        await supabase.functions.invoke('add-to-resend-audience', {
          body: { 
            email, 
            name: displayName || email.split('@')[0] 
          }
        });
      } catch (resendError) {
        console.error('Failed to add to Resend audience:', resendError);
        // Don't fail signup if Resend fails
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    // Check rate limit for auth attempts
    const rateLimitCheck = await supabase.functions.invoke('rate-limiter', {
      body: { action: 'auth', identifier: email }
    });

    if (rateLimitCheck.error || !rateLimitCheck.data?.allowed) {
      return { error: { message: 'Too many login attempts. Please try again later.' } };
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Log security event
    if (error) {
      await supabase.functions.invoke('security-logger', {
        body: {
          event_type: 'failed_login',
          event_details: { email, error: error.message }
        }
      });
    } else {
      await supabase.functions.invoke('security-logger', {
        body: {
          event_type: 'successful_login',
          event_details: { email }
        }
      });
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const signInWithGoogle = async (redirectPath?: string) => {
    // Check for pending invitations and set appropriate redirect
    const pendingTeamInvite = localStorage.getItem('pending_team_invitation_token');
    const pendingClientInvite = localStorage.getItem('pending_client_portal_invite');
    const pendingClientSession = localStorage.getItem('pending_client_portal_session');

    const safePath = redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')
      ? redirectPath
      : null;

    // Persist as fallback in case provider strips query params
    if (safePath) {
      localStorage.setItem('pendingAuthRedirect', safePath);
    }

    const isNative = Capacitor.isNativePlatform();

    const redirectTo = isNative
      ? (pendingTeamInvite || pendingClientInvite || pendingClientSession)
        ? 'com.remarkt.eventdatepicker://client-dashboard'
        : safePath
          ? `com.remarkt.eventdatepicker://${safePath.substring(1)}`
          : 'com.remarkt.eventdatepicker://dashboard'
      : (pendingTeamInvite || pendingClientInvite || pendingClientSession)
        ? `${window.location.origin}/client-dashboard`
        : safePath
          ? `${window.location.origin}${safePath}`
          : `${window.location.origin}/dashboard`;
    
    if (isNative) {
      const { Browser } = await import('@capacitor/browser');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: true
        }
      });
      
      if (error) return { error };
      
      if (data?.url) {
        await Browser.open({ url: data.url });
      }
      return { error: null };
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo
        }
      });
      return { error };
    }
  };

  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return { session, error: null };
    } catch (error: any) {
      return { session: null, error };
    }
  };

  const validateSession = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        return { isValid: false, error: error?.message || 'No authenticated user' };
      }
      return { isValid: true, error: null };
    } catch (error: any) {
      return { isValid: false, error: error.message };
    }
  };

  const resetPassword = async (email: string) => {
    console.log('Requesting password reset with redirect to:', `${window.location.origin}/auth`);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password
    });
    return { error };
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    refreshSession,
    validateSession,
    resetPassword,
    updatePassword,
  };
};